import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Close as IconClose } from '@lyearn/icons';
import { Box, Button, DeprecatedIconButton, Dialog, Input, Stack } from '@lyearn/ui';
import Loader from '@/components/Loader';
import { DEFAULT_VALUE } from '@/components/PageLayout';
import PageLayoutContextProvider from '@/components/PageLayout/context';
import { PageLayoutType } from '@/components/PageLayout/types';
import { KeyCodes } from '@/consts/keyCodes';
import QuizEditor from '@/modules/corporate/modules/quiz/views/QuizEditor';

import { EmbeddedQuizEditorContextType, EmbeddedQuizEditorProvider } from './context';
import useCreateQuizInBlock from './useCreateQuizInBlock';

const DIALOG_CLASSES = {
  paper: 'my-32 mx-24 w-full max-w-none h-full rounded-8 border border-border',
};

interface QuizEditorModalProps {
  open: boolean;
  contentId?: string;
  updateAttribute: (args: object) => void;
  onClose: () => void;
}

function QuizNameForm({
  creating,
  onCreate,
}: {
  creating: boolean;
  onCreate: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');

  const handleCreate = useCallback(() => {
    if (name.trim()) {
      onCreate(name.trim());
    }
  }, [name, onCreate]);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const onKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === KeyCodes.Enter) {
        handleCreate();
      }
    },
    [handleCreate],
  );

  return (
    <Stack className="h-full px-32" direction="col" justifyContent="center">
      <Input
        autoFocus
        className="my-22 w-full border-none"
        placeholder={t('Common:CreatorForm.NamePlaceholder')}
        value={name}
        variant="display-xxxl"
        onChange={onChange}
        onKeyPress={onKeyPress}
      />
      {name.trim() ? (
        <Button
          className="mt-64 self-start"
          loading={creating}
          shape="pill"
          size="x-large"
          onClick={handleCreate}>
          {t('Common:CreatorForm.Action')}
        </Button>
      ) : null}
    </Stack>
  );
}

function QuizEditorModal(props: QuizEditorModalProps) {
  const { t } = useTranslation();
  const { open, contentId, updateAttribute, onClose } = props;
  const [createdQuizId, setCreatedQuizId] = useState<string | null>(null);
  const quizId = contentId ?? createdQuizId;
  const [headerProps, setHeaderProps] = useState<PageLayoutType>(DEFAULT_VALUE);

  const onCreated = useCallback((id: string) => {
    // Show the editor immediately via local state. Persisting the new quiz id to
    // the block (updateAttribute) mutates the ProseMirror node, which re-renders
    // this NodeView and tears down the open modal before the editor can mount —
    // so defer that write until the modal closes.
    setCreatedQuizId(id);
  }, []);

  const { creating, onCreate } = useCreateQuizInBlock({ onCreated });

  const handleClose = useCallback(() => {
    if (createdQuizId) {
      updateAttribute({ contentId: createdQuizId });
    }
    setCreatedQuizId(null);
    onClose();
  }, [createdQuizId, updateAttribute, onClose]);

  const embeddedContext = useMemo<EmbeddedQuizEditorContextType>(
    () => ({ isEmbedded: true, onClose: handleClose }),
    [handleClose],
  );

  return (
    <Dialog classes={DIALOG_CLASSES} open={open} onClose={handleClose}>
      <PageLayoutContextProvider setHeaderProps={setHeaderProps}>
        <EmbeddedQuizEditorProvider value={embeddedContext}>
          {quizId ? (
            <Stack className="h-full" direction="col">
              <Stack
                alignItems="center"
                className="h-[52px] flex-shrink-0 border-b border-border bg-bg-primary px-8"
                direction="row">
                <Box className="flex flex-row items-center">{headerProps.LeftNode}</Box>
                <Box className="flex flex-grow flex-row justify-end me-12">
                  {headerProps.RightNode}
                </Box>
              </Stack>
              <React.Suspense fallback={<Loader />}>
                <QuizEditor contentId={quizId} />
              </React.Suspense>
            </Stack>
          ) : (
            <>
              <DeprecatedIconButton
                appearance="secondary"
                aria-label={t('Common:Actions.CloseDialog')}
                className="absolute top-4 z-50 end-0 me-12"
                size="medium"
                variant="ghost"
                onClick={handleClose}>
                <IconClose className="!h-26 !w-26" />
              </DeprecatedIconButton>
              <QuizNameForm creating={creating} onCreate={onCreate} />
            </>
          )}
        </EmbeddedQuizEditorProvider>
      </PageLayoutContextProvider>
    </Dialog>
  );
}

export default QuizEditorModal;
